/**
 * Cancelled Item Card Component
 *
 * Disposition of a garment whose order left this location:
 *  - BLANK (unprinted)  → Return to shelf.
 *  - PRINTED (good)     → Add to pre-printed stock (reuse)  OR  Discard.
 * Visually mirrors the Print Files issue card (PrintFileIssuesList.tsx) —
 * thumbnail + chip row + a "why cancelled" inset panel + a footer action row —
 * so the two issue-card families read as one design language. Touch-optimized
 * (h-16 auto-open on scan). Confirms before processing.
 */
import { useState, forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { Package, XCircle, Loader2, Recycle, ImageOff, ExternalLink, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ClientOrgChip } from '@/components/ClientOrgChip';
import { ClientLabelledBadge } from '@/components/production/ClientLabelledBadge';
import { useClientOrgLabels } from '@/hooks/useClientOrgLabels';
import { useOrganization } from '@/contexts/OrganizationContext';
import { getShopifyAdminOrderUrl } from '@/lib/shopify-urls';
import { useDateFnsLocale } from '@/lib/format-dates';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { CancelledItem, CancelReasonKind, LastSeenPhase } from '../types';
import { cancelledItemConfig } from '../types';

/**
 * Action type matching backend CancelledItemActionSchema.
 */
type CancelledItemAction = 'return_to_shelf' | 'add_to_preprint' | 'mark_defect';

/**
 * "Why this garment has been cancelled" copy per provenance kind — used only
 * when the cancelling event carried no free-text reason, except for a DTF/DTG
 * switch, whose line always shows with the switcher's note under it.
 */
const WHY_KEYS: Record<CancelReasonKind, string> = {
  order_cancelled: 'issues.cancelled.whyOrderCancelled',
  order_moved: 'issues.cancelled.whyOrderMoved',
  order_change: 'issues.cancelled.whyOrderChangeFallback',
  print_technology_switch: 'issues.cancelled.whyPrintTechnologySwitch',
};

/**
 * "Last seen" copy per phase — explicit literal keys (not a template string)
 * so check:i18n can see them. BIN interpolates `{{bin}}`; a BIN row whose
 * label is gone (routed cancels clear bin_id) falls back to binUnknown.
 */
const LAST_SEEN_KEYS: Record<LastSeenPhase, string> = {
  SHELF: 'issues.cancelled.lastSeen.shelf',
  PICKING: 'issues.cancelled.lastSeen.picking',
  PRETREAT: 'issues.cancelled.lastSeen.pretreat',
  FILM_PRINT: 'issues.cancelled.lastSeen.filmPrint',
  PRINT: 'issues.cancelled.lastSeen.print',
  PRESS: 'issues.cancelled.lastSeen.press',
  CATCHING: 'issues.cancelled.lastSeen.catching',
  BIN: 'issues.cancelled.lastSeen.bin',
  PACKING: 'issues.cancelled.lastSeen.packing',
  PACKING_BENCH: 'issues.cancelled.lastSeen.packingBench',
};

/** Confirm-button colour per action; empty = the dialog's default primary button. */
const CONFIRM_CLASSNAMES: Record<CancelledItemAction, string> = {
  return_to_shelf: '',
  add_to_preprint: 'bg-emerald-600 text-white hover:bg-emerald-700',
  mark_defect: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
};

interface CancelledItemCardProps {
  item: CancelledItem;
  onProcess: (garmentId: string, action: CancelledItemAction) => void;
  isProcessing?: boolean;
  /** When highlighted (from scanner), auto-open the confirmation for single-action (BLANK) items */
  highlighted?: boolean;
  /** Notify parent when confirmation dialog opens/closes */
  onDialogOpenChange?: (open: boolean) => void;
  /** BLU-717 S3: pre-print creation is disabled at this floor. Gate ONLY on
   *  this (never on the raw `enabled` flag it derives from) — `usePreprintPermission`
   *  fails OPEN, so an unknown/unreadable state must keep the action live. */
  preprintBlocked?: boolean;
  /** The floor's name for the disabled-reason copy, when known. */
  locationName?: string | null;
  /** Bulk-selection checkbox state — present only while the list's selection
   *  toolbar is active (see `onToggleSelect`). */
  selected?: boolean;
  /** Fired when the row's checkbox is clicked; `shiftKey` drives the parent's
   *  range-select. Omit to render the card exactly as before (no checkbox). */
  onToggleSelect?: (shiftKey: boolean) => void;
}

export const CancelledItemCard = forwardRef<HTMLDivElement, CancelledItemCardProps>(
  function CancelledItemCard(
    {
      item,
      onProcess,
      isProcessing = false,
      highlighted = false,
      onDialogOpenChange,
      preprintBlocked = false,
      locationName = null,
      selected = false,
      onToggleSelect,
    },
    ref
  ) {
    const { t } = useTranslation('production');
    const { organization } = useOrganization();
    const [pendingAction, setPendingAction] = useState<CancelledItemAction | null>(null);
    // The pressed action survives past confirm (unlike pendingAction, cleared
    // on confirm) so the correct button — not just "a" button — shows the
    // in-flight spinner while `isProcessing` catches up asynchronously.
    const [lastAction, setLastAction] = useState<CancelledItemAction | null>(null);
    const [imgFailed, setImgFailed] = useState(false);
    const isBlank = item.item_type === 'BLANK';
    // BLU-3PL: cross-org chip when a 3PL owner views a client's cancelled item.
    const { isCrossOrg, labelFor } = useClientOrgLabels();
    const rowOrgId = item.organization_id;
    // The shirt goes back on the backup's pile, so the card names that SKU.
    const pickSku = item.pick_sku ?? item.sku;

    // Notify parent of dialog state changes
    useEffect(() => {
      onDialogOpenChange?.(pendingAction !== null);
    }, [pendingAction, onDialogOpenChange]);

    // Auto-open the confirmation when highlighted (from scanner) — only for BLANK items,
    // which have a single action. PRINTED items have two choices (reuse vs discard), so
    // we scroll + ring and let the operator pick (always-prompt).
    useEffect(() => {
      if (highlighted && !isProcessing && isBlank) {
        const timer = setTimeout(() => setPendingAction('return_to_shelf'), 300);
        return () => clearTimeout(timer);
      }
      return undefined;
    }, [highlighted, isProcessing, isBlank]);

    const config = cancelledItemConfig[item.item_type];
    const dateLocale = useDateFnsLocale();
    const waitingTime = formatDistanceToNow(new Date(item.created_at), {
      addSuffix: true,
      locale: dateLocale,
    });

    // Per-action labels.
    const actionLabel: Record<CancelledItemAction, string> = {
      return_to_shelf: t('issues.cancelled.returnToShelf'),
      add_to_preprint: t('issues.cancelled.addToPreprint'),
      mark_defect: t('issues.cancelled.discard'),
    };
    const actionVerb: Record<CancelledItemAction, string> = {
      return_to_shelf: t('issues.cancelled.returnToShelfVerb'),
      add_to_preprint: t('issues.cancelled.addToPreprintVerb'),
      mark_defect: t('issues.cancelled.discardVerb'),
    };

    const handleConfirm = () => {
      if (!pendingAction) return;
      setLastAction(pendingAction);
      onProcess(item.id, pendingAction);
      setPendingAction(null);
    };

    // Cross-org rows deep-link the wrong store — only build the URL for the
    // caller's own org (fail-closed null → plain text).
    const orderUrl =
      item.organization_id === organization?.id
        ? getShopifyAdminOrderUrl(organization?.shopify_domain, item.shopify_order_id)
        : null;

    const whyText =
      item.cancel_reason_kind === 'print_technology_switch'
        ? [t(WHY_KEYS.print_technology_switch), item.cancel_reason_text].filter(Boolean).join('\n')
        : item.cancel_reason_text ?? t(WHY_KEYS[item.cancel_reason_kind]);

    // "Last seen" — where to physically look for this garment. Phase comes
    // from the backend (status-derived). The key lookup is guarded so a
    // backend-ahead-of-client deploy (new phase value, cached bundle) renders
    // nothing instead of t(undefined). A BIN row with no label (routed
    // cancels clear bin_id) degrades to the label-less "in a bin" copy.
    const lastSeenKey =
      item.last_seen_phase === 'BIN' && !item.bin_label
        ? 'issues.cancelled.lastSeen.binUnknown'
        : item.last_seen_phase
          ? LAST_SEEN_KEYS[item.last_seen_phase]
          : undefined;
    const lastSeenText = lastSeenKey ? t(lastSeenKey, { bin: item.bin_label ?? '' }) : null;
    const lastSeenAgo = item.last_seen_at
      ? formatDistanceToNow(new Date(item.last_seen_at), { addSuffix: true, locale: dateLocale })
      : null;

    return (
      <>
        <Card
          ref={ref}
          className={cn(
            'transition-all duration-300 motion-reduce:transition-none',
            highlighted && 'ring-2 ring-primary ring-offset-2',
            // The scan highlight outranks the selection ring — never both.
            !highlighted && selected && 'ring-1 ring-threadheads-purple/40'
          )}
        >
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              {/* Selection checkbox — only when the list's selection toolbar is active */}
              {onToggleSelect && (
                <div
                  className="flex h-16 shrink-0 items-center"
                  // Capture shiftKey from the underlying click before the Checkbox
                  // fires its change handler.
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelect(e.shiftKey);
                  }}
                >
                  <Checkbox
                    checked={selected}
                    aria-label={t('issues.cancelled.bulk.selectRow', {
                      name: item.product_name ?? t('issues.printFiles.unknownProduct'),
                    })}
                    className="pointer-events-none"
                  />
                </div>
              )}

              {/* Thumbnail */}
              <div className="w-16 h-16 rounded-md border bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                {item.image_url && !imgFailed ? (
                  <img
                    src={item.image_url}
                    alt=""
                    loading="lazy"
                    onError={() => setImgFailed(true)}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageOff className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
                )}
              </div>

              {/* Main column */}
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-base leading-tight truncate">
                      {item.product_name ?? t('issues.printFiles.unknownProduct')}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      {isCrossOrg(rowOrgId) && <ClientOrgChip label={labelFor(rowOrgId)} />}
                      <Badge variant="outline" className={cn('text-xs font-mono', item.pick_sku && 'font-bold')}>
                        {pickSku}
                      </Badge>
                      {item.client_labelled && <ClientLabelledBadge clientName={item.client_name} />}
                      {item.size && (
                        <Badge variant="secondary" className="text-xs">
                          {item.size}
                        </Badge>
                      )}
                      {item.color && (
                        <Badge variant="secondary" className="text-xs">
                          {item.color}
                        </Badge>
                      )}
                      <Badge variant="outline" className={`${config.color} ${config.bgColor} border-0`}>
                        {isBlank ? t('issues.cancelled.blankLabel') : t('issues.cancelled.printedLabel')}
                      </Badge>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                    {t('issues.cancelled.createdAgo', { time: waitingTime })}
                  </span>
                </div>

                {/* Why this garment has been cancelled */}
                <div className="mt-2 rounded-md border bg-muted/50 px-2.5 py-1.5">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t('issues.cancelled.whyPanel')}
                  </p>
                  {/* A reason typed on the Order Changes tab can be several
                      sentences, one change per line: keep the line breaks, and
                      let a long product name wrap inside the panel. */}
                  <p className="mt-0.5 whitespace-pre-line break-words text-xs">{whyText}</p>
                </div>

                {/* Last seen — points the operator at the station/bin to search.
                    text-sm (not xs): this is the actionable "where", operator-
                    critical per PRODUCTION_DESIGN §10; wraps, never truncates. */}
                {lastSeenText && (
                  <p className="mt-2 flex items-start gap-1.5 text-sm">
                    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span>
                      {lastSeenText}
                      {lastSeenAgo && (
                        <span className="text-muted-foreground"> · {lastSeenAgo}</span>
                      )}
                    </span>
                  </p>
                )}

                {/* Order link + actions */}
                <div className="mt-2 flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    {item.order_name &&
                      (orderUrl ? (
                        <a
                          href={orderUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={t('issues.printFiles.viewOrder')}
                          className="inline-flex min-w-0 items-center gap-0.5 rounded-sm text-xs font-medium text-threadheads-purple hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-threadheads-purple/50"
                        >
                          <span className="truncate">{item.order_name}</span>
                          <ExternalLink className="h-3 w-3 shrink-0 opacity-70" aria-hidden="true" />
                        </a>
                      ) : (
                        <span className="truncate text-xs text-muted-foreground">{item.order_name}</span>
                      ))}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {isBlank ? (
                      <Button
                        size="sm"
                        className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
                        onClick={() => setPendingAction('return_to_shelf')}
                        disabled={isProcessing}
                      >
                        {isProcessing && lastAction === 'return_to_shelf' ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                        ) : (
                          <Package className="h-3.5 w-3.5" aria-hidden="true" />
                        )}
                        {isProcessing && lastAction === 'return_to_shelf'
                          ? t('processing')
                          : actionLabel.return_to_shelf}
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700"
                          onClick={() => setPendingAction('add_to_preprint')}
                          disabled={isProcessing || preprintBlocked}
                        >
                          {isProcessing && lastAction === 'add_to_preprint' ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                          ) : (
                            <Recycle className="h-3.5 w-3.5" aria-hidden="true" />
                          )}
                          {isProcessing && lastAction === 'add_to_preprint'
                            ? t('processing')
                            : actionLabel.add_to_preprint}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="gap-1.5"
                          onClick={() => setPendingAction('mark_defect')}
                          disabled={isProcessing}
                        >
                          {isProcessing && lastAction === 'mark_defect' ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                          ) : (
                            <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                          )}
                          {isProcessing && lastAction === 'mark_defect'
                            ? t('processing')
                            : actionLabel.mark_defect}
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* BLU-717 S3: pre-print unavailable at this floor — visible text,
                    never hover-only (mirrors InventoryTableFilters' disabled-item hint). */}
                {!isBlank && preprintBlocked && (
                  <p role="note" className="mt-1 text-[11px] leading-snug text-muted-foreground">
                    {t('issues.cancelled.preprintDisabledReason', { location: locationName ?? '' })}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <AlertDialog open={pendingAction !== null} onOpenChange={(open) => !open && setPendingAction(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('issues.cancelled.confirmAction')}</AlertDialogTitle>
              <AlertDialogDescription>
                {pendingAction && t('issues.cancelled.confirmActionDesc', { action: actionVerb[pendingAction] })}
                <br />
                <br />
                <strong>{t('issues.cancelled.orderLabel')}:</strong> {item.order_name}
                <br />
                <strong>{t('issues.cancelled.skuLabel')}:</strong> {pickSku}
                {item.client_labelled && (
                  <>
                    {' '}
                    <ClientLabelledBadge clientName={item.client_name} />
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="h-12">{t('issues.cancelled.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirm}
                className={cn('h-12', pendingAction && CONFIRM_CLASSNAMES[pendingAction])}
              >
                {pendingAction ? actionLabel[pendingAction] : ''}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }
);
